import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAfter } from 'date-fns';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { GUID, InsightView, Tenant } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';

const InsightQueryKeys = {
    ALL_INSIGHTS: ['insights'],
    byTenants: (tenantGUIDs: GUID<Tenant>[]) => ['insights', 'tenants', tenantGUIDs],
} as const;

export const useListInsights = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useQuery({
        queryKey: InsightQueryKeys.byTenants(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [insights] = await CustomerApi().listInsightsByTenants(tenantGUIDs);
            return insights ?? [];
        },
    });
};

export const useActiveInsights = () => {
    const query = useListInsights();
    const data = useMemo(() => {
        const now = new Date();
        return query.data?.filter((insight) => insight.expiresAt == null || isAfter(new Date(insight.expiresAt), now));
    }, [query.data]);
    return { ...query, data };
};

// Ignoring an insight hides it for the current advisor only (server-scoped to their personOrg).
// We optimistically drop it from the cached feed so the card disappears instantly, then reconcile
// with the server on settle. Undoing calls useUnignoreInsight, which brings the insight back.
export const useIgnoreInsight = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    const queryKey = InsightQueryKeys.byTenants(tenantGUIDs);

    return useMutation({
        mutationFn: async (insightGUID: GUID<InsightView>) => {
            await CustomerApi().ignoreInsight(insightGUID);
        },
        onMutate: async (insightGUID) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<InsightView[]>(queryKey);
            queryClient.setQueryData<InsightView[]>(
                queryKey,
                (data) => data?.filter((insight) => insight.insightGUID !== insightGUID) ?? data,
            );
            return { previous };
        },
        onError: (_error, _insightGUID, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
            toast.error('Failed to ignore insight.');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};

export const useUnignoreInsight = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    const queryKey = InsightQueryKeys.byTenants(tenantGUIDs);

    return useMutation({
        mutationFn: async (insightGUID: GUID<InsightView>) => {
            await CustomerApi().unignoreInsight(insightGUID);
        },
        onError: () => {
            toast.error('Failed to restore insight.');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};
